import { Injectable } from '@angular/core';
import { CanActivate, Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { BootstrapService } from './bootstrap.service';
import { LoginService } from '../components/login/login.service';
import { ConfigService } from './config.service';

@Injectable()
export class ModuleAccessManager implements CanActivate {

    constructor(
        private _bootstrapService: BootstrapService,
        private _loginService: LoginService,
        private _router: Router,
        private _configService: ConfigService
    ) { }

    public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        this._bootstrapService.activatedRoute = route;
        return this._bootstrapService.init().map((result) => {
            if (route.data.module) {
                let hasAccess = this.hasAccess(route.data.module);
                if (!hasAccess) {
                    this._router.navigateByUrl('/dashboard');
                }
                return hasAccess;
            } else {
                return true;
            }
        });
    }

    public canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        return this.canActivate(route, state);
    }

    public hasAccess(module: any): boolean {
        if (typeof module === 'string' || module instanceof String) {
            module = [module];
        }

        // On vérifie déjà que le module existe
        let moduleIsInstalled = false;
        for (let application of this._configService.get('applicationList')) {
            let applicationHasModule = application.modules.filter((elem) => {
                return module.indexOf(elem.moduleNom) !== -1;
            });
            if (applicationHasModule.length > 0) {
                moduleIsInstalled = true;
                break;
            }
        }

        // On vérifie les droits de l'utilisateur et de son groupe
        let user = this._loginService.loggedInUser.getValue();
        if(user) {
            let userHasModule = user.modules.filter((elem) => {
                return module.indexOf(elem.moduleNom) !== -1;
            });
            let groupHasModule = user.group.modules.filter((elem) => {
                return module.indexOf(elem.moduleNom) !== -1;
            });

            if (moduleIsInstalled && (user.user_is_superadmin === 1 || (userHasModule.length > 0 || groupHasModule.length > 0))) {
                return true;
            } else {
                return false;
            }
        }

        return false;
    }
}

@Injectable()
export class ApplicationAccessManager implements CanActivate {

    constructor(
        private _bootstrapService: BootstrapService,
        private _loginService: LoginService,
        private _router: Router,
        private _configService: ConfigService
    ) { }

    public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        this._bootstrapService.activatedRoute = route;
        return this._bootstrapService.init().map((result) => {
            if (route.data.application !== '') {
                let hasAccess = this.hasAccess(route.data.application);
                if (!hasAccess) {
                    if(route.data.defaultApplication && route.data.defaultApplication !== route.data.application){
                        // If a default application is set, redirect to this application instead of going back to login screen
                        let defaultApplication = this._configService.get('applicationList').filter((elem) => {
                            return elem.label === route.data.defaultApplication;
                        })
                        if(defaultApplication.length>0 && defaultApplication[0].path){
                            // Default application was found
                            window.location.href = defaultApplication[0].path;
                        }
                        else{
                            // Default application was not found, fall back to default behavior : go back to login screen
                            this._loginService.logout();
                            window.location.reload();
                        }
                    }
                    else{
                        // Default behavior : go back to login screen
                        this._loginService.logout();
                        window.location.reload();
                    }
                }
                return hasAccess;
            } else {
                return true;
            }
        });
    }

    public canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        return this.canActivate(route, state);
    }

    public hasAccess(application: string): boolean {
        // On vérifie déjà que l'application est installée
        let applicationIsInstalled = this._configService.get('applicationList').filter((elem) => {
            return elem.label === application;
        });

        // On vérifie les droits de l'utilisateur et de son groupe
        let user = this._loginService.loggedInUser.getValue();
        let userHasApplication = user.applications.filter((elem) => {
            return elem.label === application;
        });
        let groupHasApplication = user.group.applications.filter((elem) => {
            return elem.label === application;
        });

        if (applicationIsInstalled && (user.user_is_superadmin === 1 || (userHasApplication.length > 0 || groupHasApplication.length > 0))) {
            return true;
        } else {
            return false;
        }
    }
}

@Injectable()
export class VariableAccessManager implements CanActivate {
    constructor(
        private _bootstrapService: BootstrapService,
        private _configService: ConfigService,
        private _router: Router,
    ) { }

    public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        this._bootstrapService.activatedRoute = route;

        return this._bootstrapService.init().map((result) => {
            if (route.data.variable !== '') {
                let hasAccess = this.hasAccess(route.data.variable);

                if (!hasAccess) {
                    this._router.navigateByUrl('../');
                }

                return hasAccess;
            } else {
                return true;
            }
        });
    }

    public canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        return this.canActivate(route, state);
    }

    public hasAccess(requiredVars: any[]): boolean {
        // On récupère la liste des admin_variables récupérées.
        let variables: any = this._configService.get('variables');

        let allValid: boolean = undefined;

        for (let curModule of requiredVars) {
            if (variables[curModule.module]) {
                for (let curVar of curModule.variables) {
                    let curValue: any = variables[curModule.module][curVar.name];

                    if (curVar.type == 'boolean') {
                        curValue = curValue == 'true';
                    }

                    let isValid: boolean = curValue === curVar.expected;

                    if (allValid == undefined) {
                        allValid = isValid;
                    } else {
                        allValid = allValid && isValid;
                    }

                    if (!allValid) {
                        break;
                    }
                }
            } else {
                allValid = false;
            }

            if (!allValid) {
                break;
            }
        }

        return allValid;
    }
}

@Injectable()
export class UserIsAdminAccessManager implements CanActivate {
    constructor(
        private _bootstrapService: BootstrapService,
        private _loginService: LoginService,
        private _router: Router,
    ) { }

    public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        this._bootstrapService.activatedRoute = route;

        return this._bootstrapService.init().map((result) => {
            let hasAccess: boolean = this.hasAccess();

            if (!hasAccess) {
                this._router.navigateByUrl('../');
            }

            return hasAccess;
        });
    }

    public canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
        return this.canActivate(route, state);
    }

    public hasAccess(): boolean {
        let user: any = this._loginService.loggedInUser.getValue();

        return user.user_is_superadmin === 1 || user.user_is_admin === 1;
    }
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/VyQQATNu/0/groupe-dev/nm17/node_modules/nm-socle-application/app/services/rights-manager.service.ts